//NOTE: UserDefinedObject State contains two types of state
//      Config:  this is where userdefined table configuration is stored (after retreiving from SDK)
//      Table:   This is where the data is stored.   It is currently stored as as JSON object keyed on
//               the table name and contains and array of UD Records associated with that table is SKD
//               UD_Record format
//               that where each table contains an array of records
//               {
//                  TableNameX: {Table_Name: 'TableNameY',..., UD_Record [ ..., UD_Data: { ... }]},
//                  TableNameY: {Table_Name: 'TableNameX',..., UD_Record [ ..., UD_Data: { ... }]},
//               }

import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    AddUserDefinedData,
    CloneUserDefinedData,
    DeleteUserDefinedData,
    ResetState,
    RestoreUserDefinedData,
    SetUserDefinedTableConfig,
    SetUserDefinedTableData,
    UpdateUserDefinedData
} from './user-defined-object.actions';
import {
    UserDefinedParentType,
    UserDefinedRecordType,
    UserDefinedTableConfig,
    UserDefinedTableData
} from './user-defined-object.types';

export interface UserDefinedObjectStateModel {
    table: { [Table_Name: string]: UserDefinedTableData }; //TODO -- ADD TYPE
    config: { [udObjectCode: string]: UserDefinedTableConfig };
    // currentEntityID: number;
    // currentContractID: number;
    // currentClaimID: number;
}

const UserDefinedObjectStateDefaults: UserDefinedObjectStateModel = {
    table: {},
    config: {}
    // currentEntityID: null,
    // currentContractID: null,
    // currentClaimID: null
};

@State<UserDefinedObjectStateModel>({
    name: 'userDefinedObjectState',
    defaults: UserDefinedObjectStateDefaults
    //children: []
})
@Injectable()
export class UserDefinedObjectState {
    constructor(private helper: StateHelperService, private app: AppService, private store: Store) { }

    //#region //************** SELECTORS  ***************

    @Selector()
    static userDefinedTableData(state: UserDefinedObjectStateModel) {
        return state.table;
    }

    @Selector()
    static userDefinedTableDataAsArray(state: UserDefinedObjectStateModel) {
        let result: any = [];
        for (var i in state.table) {
            result.push(state.table[i]);
        }
        return result;
    }

    @Selector()
    static userDefinedTableDataAsStateEntities(state: UserDefinedObjectStateModel) {
        //returns UD Data in stateEntities format:   TableName {Record_ID: Record, Record_ID: Record...}
        let result: any = {};
        for (var tableName in state.table) {
            result[tableName] = {};
            state.table[tableName].UD_Record.forEach((record) => {
                result[tableName][record.UD_Record_ID] = record;
            });
        }
        return result;
    }

    @Selector()
    static userDefinedRecordsByTable(state: UserDefinedObjectStateModel) {
        return (tableName: string) => {
            let result: any = [];
            for (var i in state.table) {
                if (state.table[i].Table_Name == tableName) {
                    for (var j in state.table[i].UD_Record) {
                        // add table name to UD data row to assist in update action
                        result.push({ Table_Name: state.table[i].Table_Name, ...state.table[i].UD_Record[j] });
                    }
                }
            }
            return result;
        };
    }

    @Selector()
    static userDefinedRecordCountByTable(state: UserDefinedObjectStateModel) {
        return (tableName: string) => {
            let count = 0;
            for (var i in state.table) {
                if (state.table[i].Table_Name == tableName) {
                    for (var j in state.table[i].UD_Record) {
                        count++;
                    }
                }
            }
            return count;
        };
    }

    @Selector()
    static userDefinedTableConfig(state: UserDefinedObjectStateModel) {
        return state.config;
    }

    @Selector()
    static userDefinedTableConfigByObject(state: UserDefinedObjectStateModel) {
        return (objectCode: string) => {
            if (state.config[objectCode]) {
                return state.config[objectCode];
            }
            return null;
        };
    }

    @Selector()
    static userDefinedTableConfigByTableName(state: UserDefinedObjectStateModel) {
        return (tableName: string) => {
            for (var i in state.config) {
                if (state.config[i].dbTableName == tableName) {
                    return state.config[i];
                }
            }
        };
    }

    //#endregion

    //******  ACTIONS  ******
    public getConfigFromTableName(state: UserDefinedObjectStateModel, tableName: string): UserDefinedTableConfig {
        for (var i in state.config) {
            if (state.config[i].dbTableName == tableName) {
                return state.config[i];
            }
        }
        return null;
    }

    public columnExistsInTable(state: UserDefinedObjectStateModel, tableName: string, columnName: string): boolean {
        for (var i in state.config) {
            if (state.config[i].dbTableName == tableName) {
                let table = state.config[i];
                for (var column in table.fieldConfig) {
                    if (table.fieldConfig[column].key == columnName) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    @Action(SetUserDefinedTableConfig)
    setUserDefinedTableConfig(sc: StateContext<UserDefinedObjectStateModel>, action: SetUserDefinedTableConfig) {
        const state = sc.getState();

        // TODO:  DO MANUAL NORMALIZING?  OR JUST UPDATE BY SEARCHING ARRAYS
        //  DROP ENTITIES

        sc.patchState({
            config: { ...state.config, [action.udTableConfig.objectCode]: action.udTableConfig }
        });
    }

    @Action(SetUserDefinedTableData)
    setUserDefinedTableData(sc: StateContext<UserDefinedObjectStateModel>, action: SetUserDefinedTableData) {
        const state = sc.getState();
        sc.patchState({
            table: action.udTableData || {}
            // currentEntityID: action.entityID,
            // currentContractID: action.contractID,
            // currentClaimID: action.claimID
        });
    }

    @Action(ResetState)
    resetState(sc: StateContext<UserDefinedObjectStateModel>, action: ResetState) {
        sc.patchState({
            table: {}
            // currentEntityID: null,
            // currentContractID: null,
            // currentClaimID: null
        });
    }

    @Action(UpdateUserDefinedData)
    updateUserDefinedData(sc: StateContext<UserDefinedObjectStateModel>, action: UpdateUserDefinedData) {
        const state = sc.getState();
        let tableName = action.udRecord.Table_Name;
        let recordID = action.udRecord.UD_Record_ID;
        let updateUdRecord: any = cloneDeep(action.udRecord);
        let udRecordArray: any[] = [...state.table[tableName].UD_Record];
        let rowIndex = udRecordArray.findIndex((x) => x.UD_Record_ID == recordID);

        const currentUdRecord = cloneDeep(state.table[tableName].UD_Record[rowIndex]);
        let tableConfig = this.getConfigFromTableName(state, tableName);

        this.helper.setItemChanged(updateUdRecord, state.table[tableName].UD_Record[rowIndex].Edit_Status);

        if (tableConfig.parentObjectCode == UserDefinedParentType.Contract) {
            if (
                this.columnExistsInTable(state, tableName, 'loc_id') &&
                this.columnExistsInTable(state, tableName, 'sub_loc_id')
            ) {
                if (this.helper.stateValueChanged(updateUdRecord.UD_Data?.loc_id, currentUdRecord.UD_Data?.loc_id)) {
                    updateUdRecord.UD_Data.sub_loc_id = '';
                }
            }
        }

        udRecordArray[rowIndex] = updateUdRecord;

        sc.patchState({
            table: {
                ...state.table,
                [tableName]: {
                    ...state.table[tableName],
                    UD_Record: udRecordArray
                }
            }
        });
    }

    @Action(DeleteUserDefinedData)
    deleteUserDefinedData(sc: StateContext<UserDefinedObjectStateModel>, action: DeleteUserDefinedData) {
        const state = sc.getState();
        let tableName = action.udRecord.Table_Name;
        let recordID = action.udRecord.UD_Record_ID;
        let updateUdRecord: any = cloneDeep(action.udRecord);
        let udRecordArray: any[] = [...state.table[tableName].UD_Record];
        let rowIndex = udRecordArray.findIndex((x) => x.UD_Record_ID == recordID);

        if (updateUdRecord.UD_Record_ID.startsWith('-')) {
            //if an unsaved record (negative id) then delete
            udRecordArray = [...udRecordArray.slice(0, rowIndex), ...udRecordArray.slice(rowIndex + 1)];
        } else {
            updateUdRecord.Edit_Status = 'D';
            udRecordArray[rowIndex] = updateUdRecord;
        }

        sc.patchState({
            table: {
                ...state.table,
                [tableName]: {
                    ...state.table[tableName],
                    UD_Record: udRecordArray
                }
            }
        });
    }

    @Action(RestoreUserDefinedData)
    restoreUserDefinedData(sc: StateContext<UserDefinedObjectStateModel>, action: RestoreUserDefinedData) {
        const state = sc.getState();
        let tableName = action.udRecord.Table_Name;
        let recordID = action.udRecord.UD_Record_ID;
        let updateUdRecord: any = cloneDeep(action.udRecord);
        let udRecordArray: any[] = [...state.table[tableName].UD_Record];
        let rowIndex = udRecordArray.findIndex((x) => x.UD_Record_ID == recordID);

        updateUdRecord.Edit_Status = 'C';
        udRecordArray[rowIndex] = updateUdRecord;

        sc.patchState({
            table: {
                ...state.table,
                [tableName]: {
                    ...state.table[tableName],
                    UD_Record: udRecordArray
                }
            }
        });
    }

    @Action(CloneUserDefinedData)
    cloneUserDefinedData(sc: StateContext<UserDefinedObjectStateModel>, action: CloneUserDefinedData) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let tableName = action.udRecord.Table_Name;
        let tableConfig = this.getConfigFromTableName(state, tableName);
        let newUdRecord: any = cloneDeep(action.udRecord);
        let udRecordArray: any[] = [...state.table[tableName].UD_Record];
        let idColumn = tableConfig.dbIDColumn.toLowerCase();
        let guidColumn = tableConfig.dbGUIDColumn.toLowerCase();

        newUdRecord.Edit_Status = 'A';
        newUdRecord.UD_Record_ID = newID;
        newUdRecord.UD_Record_GUID = null;
        delete newUdRecord.UD_Data[idColumn];
        delete newUdRecord.UD_Data[guidColumn];

        udRecordArray.push(newUdRecord);

        sc.patchState({
            table: {
                ...state.table,
                [tableName]: {
                    ...state.table[tableName],
                    UD_Record: udRecordArray
                }
            }
        });
    }

    @Action(AddUserDefinedData)
    addUserDefinedData(sc: StateContext<UserDefinedObjectStateModel>, action: AddUserDefinedData) {
        const state = sc.getState();

        let tableConfig = state.config[action.udObjectCode];
        let tableName = tableConfig.dbTableName;
        let newID = this.helper.getTempID();
        let idColumn = tableConfig.dbIDColumn.toLowerCase();
        let guidColumn = tableConfig.dbGUIDColumn.toLowerCase();
        let udRecordArray: UserDefinedRecordType[] = [];

        //build new record (UD_Record node and child UD_Data node)
        let newRecord: UserDefinedRecordType = {
            Table_Name: tableName,
            UD_Record_ID: newID,
            UD_Record_GUID: null,
            Edit_Status: 'A',
            UD_Data: {}
        };

        tableConfig.fieldConfig.forEach((x) => {
            newRecord.UD_Data[x.key] = ''; //add column for each data field
        });

        //TODO:  IS THIS NEEDED???
        // Add ID's to UD_Data node.    IS THIS NEEDED?
        // newRecord.UD_Data[idColumn] = null; // newID;
        // newRecord.UD_Data[guidColumn] = null;

        // switch (tableConfig.parentObjectCode.toLowerCase()) {
        //   case 'entity':
        //     newRecord.UD_Data.entity_id = state.currentEntityID;
        //     break;
        //   case 'contract':
        //     newRecord.UD_Data.contract_id = state.currentContractID;
        //     break;
        //   case 'claim':
        //     newRecord.UD_Data.claim_id = state.currentClaimID;
        //     break;
        // }
        //END TODO:  IS THIS NEEDED?

        let tableData: UserDefinedTableData = null;

        if (state.table[tableName]) {
            tableData = { ...state.table[tableName] };
            udRecordArray = state.table[tableName].UD_Record ? [...state.table[tableName].UD_Record] : [];
        } else {
            tableData = {
                Table_Name: tableConfig.dbTableName,
                Parent_Object_Code: tableConfig.parentObjectCode,
                ID_Name: tableConfig.dbIDColumn,
                UD_Record: []
            };
            udRecordArray = [];
        }

        udRecordArray.push(newRecord);
        tableData.UD_Record = udRecordArray;

        sc.patchState({
            table: {
                ...state.table,
                [tableName]: tableData
            }
        });
    }
}
